import { useEffect, useRef, useState } from "react";
import {
  Button, Table, Tag, Tooltip, Popconfirm, message, Modal, Typography, Space, Badge,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, RocketOutlined,
  EyeOutlined, StopOutlined, ExportOutlined, KeyOutlined,
  DownloadOutlined, UploadOutlined,
} from "@ant-design/icons";
import api from "../../../../../core/axios/axiosInstance";
import FlowEditorModal   from "./FlowEditorModal";
import KeySetupModal     from "./KeySetupModal";
import FlowPreviewModal  from "./FlowPreviewModal";

const { Title, Text } = Typography;

const STATUS_COLOR = {
  LOCAL: "default", DRAFT: "blue", PUBLISHED: "green",
  DEPRECATED: "orange", BLOCKED: "red", THROTTLED: "warning",
};

// Tooltip that always renders above table overflow
const TT = ({ title, children }) => (
  <Tooltip title={title} getPopupContainer={() => document.body}>
    {children}
  </Tooltip>
);

const EXPORT_STRIP = ["_id", "__v", "companyAdmin", "metaFlowId", "metaStatus",
  "previewUrl", "previewExpiresAt", "createdAt", "updatedAt"];

const MetaFlowsPage = () => {
  const [flows, setFlows]               = useState([]);
  const [loading, setLoading]           = useState(false);
  const [editorOpen, setEditorOpen]     = useState(false);
  const [editTarget, setEditTarget]     = useState(null);
  const [keyModalOpen, setKeyModalOpen]     = useState(false);
  const [previewFlow, setPreviewFlow]       = useState(null);
  const [loadingId, setLoadingId]           = useState(null);
  const [importing, setImporting]           = useState(false);
  const importInputRef                      = useRef(null);

  const busy = (flowId, action) => loadingId === `${flowId}_${action}`;
  const setBusy = (flowId, action) => setLoadingId(`${flowId}_${action}`);
  const clearBusy = () => setLoadingId(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/meta-flows");
      setFlows(data.data || []);
    } catch {
      message.error("Failed to load flows");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handlePushToMeta = async (flow) => {
    setBusy(flow._id, "push");
    try {
      const { data } = await api.post(`/api/meta-flows/${flow._id}/push`);
      message.success("Pushed to Meta successfully");
      if (data.validationErrors?.length > 0) {
        Modal.warning({
          title: "Flow has validation errors",
          content: (
            <ul>{data.validationErrors.map((e, i) => <li key={i}>{e.message || JSON.stringify(e)}</li>)}</ul>
          ),
        });
      }
      load();
    } catch (err) {
      message.error(err.response?.data?.message || "Push failed");
    } finally {
      clearBusy();
    }
  };

  const handlePublish = async (flow) => {
    setBusy(flow._id, "publish");
    try {
      await api.post(`/api/meta-flows/${flow._id}/publish`);
      message.success("Flow published successfully");
      load();
    } catch (err) {
      const errData = err.response?.data;
      const msg = errData?.message || "Publish failed";
      if (errData?.validationErrors?.length > 0) {
        Modal.error({
          title: "Cannot publish — validation errors",
          width: 600,
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>{msg}</p>
              <ul style={{ paddingLeft: 16 }}>
                {errData.validationErrors.map((e, i) => (
                  <li key={i} style={{ color: "#dc2626", fontSize: 12 }}>
                    {e.message || e.error || JSON.stringify(e)}
                  </li>
                ))}
              </ul>
              <p style={{ marginTop: 12, fontSize: 12, color: "#6b7280" }}>
                Fix these errors in the Flow Editor, then click <strong>Push to Meta</strong> again before publishing.
              </p>
            </div>
          ),
        });
      } else {
        Modal.error({
          title: "Publish failed",
          content: (
            <div>
              <p>{msg}</p>
              {errData?.metaError?.code === 36109 && (
                <p style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                  Error 36109 means Meta has blocked this action due to business account integrity.
                  Please verify your business at <a href="https://business.facebook.com" target="_blank" rel="noreferrer">business.facebook.com</a> and ensure your WhatsApp Business account is in good standing.
                </p>
              )}
            </div>
          ),
        });
      }
    } finally {
      clearBusy();
    }
  };

  const handleDeprecate = async (flow) => {
    setBusy(flow._id, "deprecate");
    try {
      await api.post(`/api/meta-flows/${flow._id}/deprecate`);
      message.success("Flow deprecated");
      load();
    } catch (err) {
      message.error(err.response?.data?.message || "Deprecate failed");
    } finally {
      clearBusy();
    }
  };

  const handleDelete = async (flow) => {
    setBusy(flow._id, "delete");
    try {
      await api.delete(`/api/meta-flows/${flow._id}`);
      message.success("Flow deleted");
      load();
    } catch (err) {
      message.error(err.response?.data?.message || "Delete failed");
    } finally {
      clearBusy();
    }
  };

  const handlePreview = (flow) => {
    setPreviewFlow(flow);
  };

  const handleExport = (flow) => {
    const clean = { ...flow };
    EXPORT_STRIP.forEach((k) => delete clean[k]);
    const blob = new Blob([JSON.stringify(clean, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(flow.name || "flow").replace(/\s+/g, "_")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    let parsed;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      message.error("Invalid JSON file");
      return;
    }
    setImporting(true);
    try {
      await api.post("/api/meta-flows/import", parsed);
      message.success("Flow imported successfully");
      load();
    } catch (err) {
      message.error(err.response?.data?.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  const columns = [
    {
      title: "Name", dataIndex: "name", key: "name",
      render: (name, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{name}</div>
          {r.description && <div style={{ fontSize: 12, color: "#6b7280" }}>{r.description}</div>}
        </div>
      ),
    },
    {
      title: "Category", dataIndex: "category", key: "category",
      render: (c) => <Tag>{(c || "").replace(/_/g, " ")}</Tag>,
    },
    {
      title: "Screens", dataIndex: "screens", key: "screens",
      render: (s) => <Badge count={(s || []).length} color="#7c3aed" />,
    },
    {
      title: "Fields", dataIndex: "fieldMappings", key: "fieldMappings",
      render: (f) => (f || []).length,
    },
    {
      title: "Type", dataIndex: "isDynamic", key: "isDynamic",
      render: (d) => <Tag color={d ? "purple" : "cyan"}>{d ? "Dynamic" : "Static"}</Tag>,
    },
    {
      title: "Status", dataIndex: "metaStatus", key: "metaStatus",
      render: (s) => <Tag color={STATUS_COLOR[s] || "default"}>{s}</Tag>,
    },
    {
      title: "Actions", key: "actions",
      render: (_, flow) => (
        <Space size={4} wrap>
          {/* Edit */}
          {flow.metaStatus !== "DEPRECATED" && (
            <TT title={flow.metaStatus === "PUBLISHED" ? "Edit flow (will revert to Draft)" : "Edit flow"}>
              {flow.metaStatus === "PUBLISHED" ? (
                <span>
                  <Popconfirm
                    title="Editing a published flow will revert it to Draft. You'll need to re-publish after editing."
                    onConfirm={() => { setEditTarget(flow); setEditorOpen(true); }}
                    okText="Edit anyway"
                    cancelText="Cancel"
                  >
                    <Button size="small" icon={<EditOutlined />} />
                  </Popconfirm>
                </span>
              ) : (
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => { setEditTarget(flow); setEditorOpen(true); }}
                />
              )}
            </TT>
          )}

          {/* Push to Meta */}
          {flow.metaStatus !== "DEPRECATED" && flow.metaStatus !== "PUBLISHED" && (
            <TT title={flow.metaStatus === "BLOCKED" ? "Re-push to Meta (fix errors)" : "Push to Meta"}>
              <Button
                size="small"
                icon={<ExportOutlined />}
                danger={flow.metaStatus === "BLOCKED"}
                loading={busy(flow._id, "push")}
                onClick={() => handlePushToMeta(flow)}
              />
            </TT>
          )}

          {/* Publish */}
          {flow.metaStatus === "DRAFT" && (
            <TT title="Publish flow">
              <span>
                <Popconfirm
                  title="Publish this flow? Editing later will revert it back to Draft."
                  onConfirm={() => handlePublish(flow)}
                >
                  <Button
                    size="small"
                    type="primary"
                    icon={<RocketOutlined />}
                    loading={busy(flow._id, "publish")}
                  />
                </Popconfirm>
              </span>
            </TT>
          )}

          {/* Preview */}
          {(flow.screens || []).length > 0 && (
            <TT title="Preview flow">
              <Button
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handlePreview(flow)}
              />
            </TT>
          )}

          {/* Deprecate */}
          {flow.metaStatus === "PUBLISHED" && (
            <TT title="Deprecate (disable this flow)">
              <span>
                <Popconfirm
                  title="Deprecate this flow? It can no longer be used."
                  onConfirm={() => handleDeprecate(flow)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<StopOutlined />}
                    loading={busy(flow._id, "deprecate")}
                  />
                </Popconfirm>
              </span>
            </TT>
          )}

          {/* Export */}
          <TT title="Export flow as JSON">
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => handleExport(flow)}
            />
          </TT>

          {/* Delete */}
          {flow.metaStatus !== "PUBLISHED" && (
            <TT title="Delete flow">
              <span>
                <Popconfirm
                  title="Delete this flow?"
                  onConfirm={() => handleDelete(flow)}
                >
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    loading={busy(flow._id, "delete")}
                  />
                </Popconfirm>
              </span>
            </TT>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>WhatsApp Flows</Title>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Create interactive native forms that open inside WhatsApp. No browser redirect needed.
            </Text>
          </div>
          <Space>
            <TT title="Manage RSA keys for dynamic flows (data exchange endpoint)">
              <Button icon={<KeyOutlined />} onClick={() => setKeyModalOpen(true)}>
                Encryption Keys
              </Button>
            </TT>
            <Button
              icon={<UploadOutlined />}
              loading={importing}
              onClick={() => importInputRef.current?.click()}
            >
              Import Flow
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => { setEditTarget(null); setEditorOpen(true); }}
              style={{ background: "#7c3aed", borderColor: "#7c3aed" }}
            >
              Create Flow
            </Button>
          </Space>
          <input
            ref={importInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: "none" }}
            onChange={handleImportFile}
          />
        </div>

        <Table
          dataSource={flows}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 20 }}
          size="small"
          locale={{ emptyText: "No flows yet. Create your first flow!" }}
        />
      </div>

      <FlowEditorModal
        open={editorOpen}
        flow={editTarget}
        onClose={() => setEditorOpen(false)}
        onSaved={() => { setEditorOpen(false); load(); }}
      />

      <KeySetupModal
        open={keyModalOpen}
        onClose={() => setKeyModalOpen(false)}
      />

      <FlowPreviewModal
        open={!!previewFlow}
        flow={previewFlow}
        onClose={() => setPreviewFlow(null)}
      />
    </div>
  );
};

export default MetaFlowsPage;
